import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Container from "@mui/material/Container";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import CircularProgress from "@mui/material/CircularProgress";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import ToggleButton from "@mui/material/ToggleButton";
import { useTranslation } from "react-i18next";
import {
  useBoardOrders,
  useMe,
  updateOrderStatus,
  updateOrderItemTag,
  updateOrderAssignee,
  updateOrderItemWeight,
} from "../shared/api/orders";
import { useStaffList } from "../shared/api/users";
import { errorMessage } from "../shared/api/errorMessage";
import { useConfirm } from "../shared/components/ConfirmDialog";
import { useOwnerAuth } from "./OwnerAuthContext";
import { useOrderStream, mergeBoardOrder, BOARD_KEY } from "./useOrderStream";
import OrderAlertsToggle from "./OrderAlertsToggle";
import { formatMoney } from "../shared/money";
import { formatDateTime } from "../shared/date";
import type { Order, OrderItem } from "../shared/types";
import { isOwnerLevel } from "./roles";

/** The single forward step offered per status; Cancel is always available for an active order. */
const NEXT_STEP: Record<string, { to: string; labelKey: string } | undefined> =
  {
    NEW: { to: "PREPARING", labelKey: "owner.board.startPreparing" },
    PREPARING: { to: "READY", labelKey: "owner.board.markReady" },
    READY: { to: "DELIVERED", labelKey: "owner.board.markDelivered" },
  };

/** One horizontal lane per active status, in pipeline order; terminal orders have left the board.
 *  The lane label is the translated `status.<STATUS>` string. */
const LANES: { status: string; color: "info" | "warning" | "success" }[] = [
  { status: "NEW", color: "info" },
  { status: "PREPARING", color: "warning" },
  { status: "READY", color: "success" },
];

/**
 * One ordered line, boxed so a wrapping variant stays visually tied to its product (D-073).
 * A catch-weight line also carries the real weighed amount: editable (a numberpad) when the parent
 * passes the `orderId`+handlers (the counter board), read-only otherwise (the delivery view).
 */
function OrderItemLine({
  item,
  orderId,
  onSaved,
  onError,
  required,
}: {
  item: OrderItem;
  orderId?: string;
  onSaved?: (o: Order) => void | Promise<void>;
  onError?: (message: string) => void;
  /** When true, an empty weight/token on this line is flagged as required (D-103). */
  required?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        px: 1,
        py: 0.75,
        // Plain "Paper" tile — the card surface colour plus a light shadow to lift each line off the
        // card. No brand tint (works the same in light and dark mode).
        bgcolor: "background.paper",
        boxShadow: 1,
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {item.productName} × {item.quantity}
      </Typography>
      {item.options && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {item.options}
        </Typography>
      )}
      {/* Editable numberpad when this line is currently sold by weight (soldByWeight is read live
          from the product, D-101 — so a just-toggled product shows the field on its existing orders).
          In the read-only view (delivery row / history) just show a weight that was already recorded,
          regardless of the current flag. */}
      {orderId && onSaved && onError
        ? item.soldByWeight && (
            <WeightInput
              orderId={orderId}
              item={item}
              onSaved={onSaved}
              onError={onError}
              required={!!required}
            />
          )
        : item.actualWeight != null && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              {t("owner.board.actualWeight")}: {item.actualWeight} kg
            </Typography>
          )}
      {/* Per-line token / leg tag (D-102): editable numberpad on the board when this line is taggable
          (tagTracking is read live from the product, D-101 — so a just-toggled product shows the
          field on its existing orders). Read-only value in the delivery/history view. */}
      {orderId && onSaved && onError
        ? item.tagTracking && (
            <TokenInput
              orderId={orderId}
              item={item}
              onSaved={onSaved}
              onError={onError}
              required={!!required}
            />
          )
        : item.tagNumber && (
            <Typography
              variant="caption"
              sx={{ display: "block", fontWeight: 700, color: "primary.main" }}
            >
              {t("owner.board.tagNumber")}: {item.tagNumber}
            </Typography>
          )}
    </Box>
  );
}

/**
 * The catch-weight numberpad on a board line: staff type the real weighed amount, which re-prices
 * the line (priced shop) or is recorded (priceless). Saves on blur, like the leg-tag field.
 * `inputMode="decimal"` opens the phone keypad with a dot; `type="number"` would add spinners.
 */
function WeightInput({
  orderId,
  item,
  onSaved,
  onError,
  required,
}: {
  orderId: string;
  item: OrderItem;
  onSaved: (o: Order) => void | Promise<void>;
  onError: (message: string) => void;
  required: boolean;
}) {
  const { t } = useTranslation();
  // ponytail: seeded once from the prop like the tag field — a weight typed on a second phone
  // won't appear here until the card remounts. One counter, one phone.
  const [value, setValue] = useState(
    item.actualWeight != null ? String(item.actualWeight) : "",
  );
  const [busy, setBusy] = useState(false);
  const missing = required && value.trim() === "";

  const save = async () => {
    const trimmed = value.trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    // Reject junk / non-positive; put the last saved value back on screen.
    if (parsed !== null && (!Number.isFinite(parsed) || parsed <= 0)) {
      setValue(item.actualWeight != null ? String(item.actualWeight) : "");
      return;
    }
    if ((parsed ?? null) === (item.actualWeight ?? null)) return;
    setBusy(true);
    try {
      await onSaved(await updateOrderItemWeight(orderId, item.id, parsed));
    } catch (err) {
      onError(errorMessage(err, t("owner.board.weightError")));
      setValue(item.actualWeight != null ? String(item.actualWeight) : "");
    } finally {
      setBusy(false);
    }
  };

  return (
    <TextField
      label={t("owner.board.actualWeight")}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) =>
        e.key === "Enter" && (e.target as HTMLInputElement).blur()
      }
      disabled={busy}
      required={required}
      error={missing}
      helperText={missing ? t("common.required") : undefined}
      size="small"
      sx={{ mt: 0.75, maxWidth: 150 }}
      inputProps={{ inputMode: "decimal", pattern: "[0-9.]*", maxLength: 8 }}
    />
  );
}

/**
 * The per-line token / leg-tag numberpad on a board line (D-102): staff type the tag tied to this
 * line's bird. Saves on blur, like the weight field. `inputMode="numeric"` opens the phone keypad;
 * `type="number"` would add spinners and eat leading zeros.
 */
function TokenInput({
  orderId,
  item,
  onSaved,
  onError,
  required,
}: {
  orderId: string;
  item: OrderItem;
  onSaved: (o: Order) => void | Promise<void>;
  onError: (message: string) => void;
  required: boolean;
}) {
  const { t } = useTranslation();
  // ponytail: seeded once from the prop like the weight field — a tag typed on a second phone
  // won't appear here until the card remounts. One counter, one phone.
  const [value, setValue] = useState(item.tagNumber ?? "");
  const [busy, setBusy] = useState(false);
  const missing = required && value.trim() === "";

  const save = async () => {
    const trimmed = value.trim();
    if (trimmed === (item.tagNumber ?? "")) return;
    setBusy(true);
    try {
      await onSaved(await updateOrderItemTag(orderId, item.id, trimmed));
    } catch (err) {
      onError(errorMessage(err, t("owner.board.tagError")));
      setValue(item.tagNumber ?? ""); // don't leave a value on screen that was never saved
    } finally {
      setBusy(false);
    }
  };

  return (
    <TextField
      label={t("owner.board.tagNumber")}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) =>
        e.key === "Enter" && (e.target as HTMLInputElement).blur()
      }
      disabled={busy}
      required={required}
      error={missing}
      helperText={missing ? t("common.required") : undefined}
      size="small"
      sx={{ mt: 0.75, maxWidth: 150 }}
      inputProps={{ inputMode: "numeric", pattern: "[0-9]*", maxLength: 16 }}
    />
  );
}

export default function BoardPage() {
  const { t } = useTranslation();
  const { isAuthenticated, session } = useOwnerAuth();
  const { data: orders, isLoading, isError } = useBoardOrders(isAuthenticated);
  const { data: me } = useMe(isAuthenticated);
  // Real-time push (SSE): merges new/updated orders into the cache, chimes + highlights new ones.
  // Fed the FULL list, never the view-filtered one — else "My deliveries" would silence the chime
  // for every order assigned to someone else.
  const { newOrderIds, status } = useOrderStream(isAuthenticated, orders);
  const [error, setError] = useState<string | null>(null);
  // "All orders" is the counter's board (the lanes below); "My deliveries" is READY orders assigned
  // to me — the ones I can take out right now. Assignment happens in PREPARING and only
  // READY→DELIVERED is legal, so a PREPARING row would just be one I can't action; the user asked
  // for READY only. Filtered on assignedStaffId, not role, so a future RIDER login lands here for
  // free (D-011). `!!me` guards the window before /auth/me resolves — without it an unassigned
  // order (assignedStaffId undefined) would match `me?.userId` (also undefined).
  // Delivery staff live in "My deliveries" — it's their whole job on this screen — so they land
  // there; the owner works the counter and defaults to the full board. Either can toggle.
  // `?view=all` overrides: the Telegram post's "enter weight / token" link (D-111) needs the lanes,
  // and a STAFF member landing on My deliveries would not find the NEW order they came for.
  const [view, setView] = useState<"all" | "mine">(
    new URLSearchParams(window.location.search).get("view") === "all" ||
      session?.role !== "STAFF"
      ? "all"
      : "mine",
  );
  const isMine = (o: Order) => !!me && o.assignedStaffId === me.userId;
  const myDeliveries = (orders ?? []).filter(
    (o) => isMine(o) && o.status === "READY",
  );

  return (
    <Container maxWidth="lg" sx={{ py: 2, pb: 8 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={700}>
          {t("owner.board.title")}
        </Typography>
        <OrderAlertsToggle />
      </Stack>
      {/* A dropped stream must never look like a quiet shop: without this the board renders
          identically whether it is live or has been disconnected for an hour. */}
      {status === "offline" && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("owner.board.offline")}
        </Alert>
      )}
      {isLoading ? (
        <CircularProgress />
      ) : isError ? (
        <Typography color="error">{t("owner.board.loadError")}</Typography>
      ) : (
        <>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, next) => next && setView(next)}
            size="small"
            color="primary"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all">{t("owner.board.viewAll")}</ToggleButton>
            <ToggleButton value="mine">
              {t("owner.board.viewMine", { count: myDeliveries.length })}
            </ToggleButton>
          </ToggleButtonGroup>
          {view === "mine" ? (
            <DeliveryList orders={myDeliveries} onError={setError} />
          ) : (
            <Stack spacing={3}>
              {LANES.map((lane) => (
                <StatusLane
                  key={lane.status}
                  label={t(`status.${lane.status}`)}
                  color={lane.color}
                  orders={(orders ?? []).filter(
                    (o) => o.status === lane.status,
                  )}
                  newOrderIds={newOrderIds}
                  onError={setError}
                />
              ))}
            </Stack>
          )}
        </>
      )}
      <Snackbar
        open={error !== null}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}

function StatusLane({
  label,
  color,
  orders,
  newOrderIds,
  onError,
}: {
  label: string;
  color: "info" | "warning" | "success";
  orders: Order[];
  newOrderIds: Set<string>;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  // When a card is advanced into this lane it lands near the left (newest-first), but a DOM
  // mutation in a scroll-snap / horizontally-scrolled row makes the browser re-align and jump
  // right, hiding it. So on gaining a card, scroll this lane back to the start to reveal it. Only
  // on gain (not every board update) so a user reading older cards to the right isn't yanked back.
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    const gained = orders.some((o) => !prevIds.current.has(o.id));
    prevIds.current = new Set(orders.map((o) => o.id));
    if (gained) scrollRef.current?.scrollTo({ left: 0 });
  }, [orders]);
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography
          variant="subtitle1"
          fontWeight={800}
          sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
        >
          {label}
        </Typography>
        <Chip label={orders.length} size="small" color={color} />
      </Stack>
      <Divider sx={{ borderBottomWidth: 2, mb: 1.5 }} />
      {orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("owner.board.noOrders")}
        </Typography>
      ) : (
        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            pb: 1,
            // An advanced order is prepended (newest-first) to its new lane; without this the
            // browser's scroll anchoring bumps scrollLeft right to keep the old cards put, hiding
            // the just-arrived card off-screen left. Disable it so the new card stays visible.
            overflowAnchor: "none",
            // Swipe/scroll right to see the rest; cards snap for a tidy feel on touch.
            scrollSnapType: "x proximity",
            "& > *": { scrollSnapAlign: "start" },
            "&::-webkit-scrollbar": { height: 8 },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "rgba(0,0,0,0.22)",
              borderRadius: 4,
            },
          }}
        >
          {orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              isNew={newOrderIds.has(order.id)}
              onError={onError}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function OrderCard({
  order,
  isNew,
  onError,
}: {
  order: Order;
  isNew: boolean;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const { isAuthenticated, session } = useOwnerAuth();
  // Same query key as BoardPage's, so N cards still make one /auth/me request.
  const { data: me } = useMe(isAuthenticated);
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  // Naming the deliverer is offered on delivery orders only, and only while they're being
  // prepared — the point is to settle it before the order is marked ready. OWNER-only, because
  // the list of names comes from the OWNER-only /api/users (the backend gates the PATCH the same
  // way). One shared TanStack query key, so N cards still make one request.
  const isOwner = isOwnerLevel(session?.role);
  const canAssign =
    isOwner &&
    order.fulfillmentType === "DELIVERY" &&
    order.status === "PREPARING";
  // Also fetched once the order has moved past PREPARING: the card still has to say who is
  // taking it out, and the name only exists in this list (the order carries the bare id).
  const { data: staff } = useStaffList(
    isOwner && (canAssign || !!order.assignedStaffId),
  );
  const assignedUser = staff?.find((u) => u.id === order.assignedStaffId);
  const isMine = !!me && order.assignedStaffId === me.userId;
  // Staff don't load the (owner-only) staff list, so for them only "You" can be named.
  const assignedName =
    (assignedUser && (assignedUser.name || assignedUser.email)) ||
    (isMine ? t("owner.board.you") : undefined);
  // A delivery order can't be marked ready until someone is named to take it out — for everyone
  // (D-112; the backend enforces it too). Blocks the "Mark ready" tap.
  const needsAssignee =
    order.fulfillmentType === "DELIVERY" &&
    order.status === "PREPARING" &&
    !order.assignedStaffId;
  // Staff can't pick someone else, but they can take an unassigned delivery themselves, so an
  // absent owner never leaves orders stuck in PREPARING (D-112).
  const canTakeIt = !isOwner && needsAssignee && !!me;
  const next = NEXT_STEP[order.status];
  // Weight and token are required (D-103): a sold-by-weight line needs its weight and a taggable line
  // its token before the order can move forward. Blocks the advance button (the backend enforces it
  // too) and flags the empty fields; Cancel is never blocked.
  const lineDetailsMissing = order.items.some(
    (i) =>
      (i.soldByWeight && i.actualWeight == null) ||
      (i.tagTracking && !i.tagNumber),
  );

  /** Writes the merged order into the board cache — see `advance` for why the cancel comes first. */
  const applyToCache = async (updated: Order) => {
    await queryClient.cancelQueries({ queryKey: BOARD_KEY });
    queryClient.setQueryData<Order[]>(BOARD_KEY, (prev) =>
      mergeBoardOrder(prev, updated),
    );
  };

  const assign = async (staffId: string) => {
    setBusy(true);
    try {
      await applyToCache(await updateOrderAssignee(order.id, staffId || null));
    } catch (err) {
      onError(errorMessage(err, t("owner.board.assignError")));
    } finally {
      setBusy(false);
    }
  };

  const advance = async (to: string) => {
    setBusy(true);
    try {
      // Apply the server's own response: the card moves immediately instead of waiting on a
      // second round-trip, which is what made a tap feel dead on a slow connection. The SSE
      // `order.updated` for this same change is idempotent with it.
      // Cancel first — an in-flight 30s poll started before this tap would otherwise resolve
      // afterwards and revert the card for up to 30s (setQueryData does not cancel fetches).
      await applyToCache(await updateOrderStatus(order.id, to, order.status));
    } catch (err) {
      // Never swallow this. A failed tap used to leave the button re-enabled and the screen
      // unchanged, so the shopkeeper just kept tapping.
      const conflict = axios.isAxiosError(err) && err.response?.status === 409;
      // Surface the server's own reason for a non-conflict error (e.g. the D-103 "enter the weight/
      // token" 400), falling back to the generic message; a stale-conflict keeps its own copy.
      onError(
        conflict
          ? t("owner.board.staleError")
          : errorMessage(err, t("owner.board.updateError")),
      );
      if (conflict) {
        // Someone else advanced it — pull the truth rather than leaving a stale card.
        await queryClient.invalidateQueries({ queryKey: ["board"] });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        width: 280,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow .3s, border-color .3s",
        ...(isNew && {
          borderColor: "secondary.main",
          boxShadow: 4,
          bgcolor: "rgba(229, 160, 53, 0.12)",
        }),
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 0.5 }}
        >
          <Typography fontWeight={700}>
            {t("common.orderNumber", { number: order.orderNumber })}
          </Typography>
          <Stack direction="row" spacing={0.5}>
            <Chip
              label={
                order.fulfillmentType === "PICKUP"
                  ? t("owner.fulfillment.pickup")
                  : t("owner.fulfillment.delivery")
              }
              size="small"
              color={order.fulfillmentType === "PICKUP" ? "warning" : "default"}
              variant="outlined"
            />
            {isNew && (
              <Chip
                label={t("owner.board.newChip")}
                size="small"
                color="secondary"
              />
            )}
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" noWrap>
          {order.customerName} · {order.customerPhone}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {formatDateTime(order.createdAt)}
        </Typography>
        {order.deliveryAddress && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {order.deliveryAddress}
          </Typography>
        )}
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
          {order.items.map((i, idx) => (
            <OrderItemLine
              key={`${i.productId}-${i.options ?? ""}-${idx}`}
              item={i}
              orderId={order.id}
              onSaved={applyToCache}
              onError={onError}
              required={!!next}
            />
          ))}
        </Box>
        {/* Priceless order (D-069): null total → no money on the card. */}
        {order.total != null && (
          <Typography fontWeight={700} sx={{ mt: 0.5 }}>
            {formatMoney(order.total)}
          </Typography>
        )}
        {order.notes && (
          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
            {t("owner.board.notes")}: {order.notes}
          </Typography>
        )}
        {/* Once the card leaves PREPARING the dropdown is gone, but the answer to "who's taking
            this one out" is exactly what the counter still needs — so it stays on the card as
            text for the rest of the order's life. */}
        {!canAssign && assignedName && (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ mt: 0.5 }}
          >
            {t("owner.board.deliveryBy")}: <strong>{assignedName}</strong>
          </Typography>
        )}
      </CardContent>
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {/* The per-line leg tags / token numbers live up in the item list now (D-102) — a taggable
            order can hold several birds, each its own tag — so there is no single tag box here. */}
        <Stack spacing={0.5}>
          {/* Who is taking this one out — asked while the order is being prepared, so the name is
              decided before it is handed over. No local state: the PATCH's response goes straight
              into the board cache, so the prop is the value. */}
          {canAssign && (
            <TextField
              select
              required
              label={t("owner.board.deliveryBy")}
              value={order.assignedStaffId ?? ""}
              onChange={(e) => assign(e.target.value)}
              disabled={busy}
              error={needsAssignee}
              helperText={
                needsAssignee ? t("owner.board.deliveryByRequired") : " "
              }
              size="small"
              fullWidth
              sx={{ mb: 0.5 }}
            >
              <MenuItem value="">{t("owner.board.unassigned")}</MenuItem>
              {/* Any active user — owners deliver too, not just STAFF. Plus whoever is already on
                  it, so a name doesn't vanish from the card the day that account is deactivated
                  (and MUI never gets a value outside its options). */}
              {(staff ?? [])
                .filter((u) => u.active || u.id === order.assignedStaffId)
                .map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.name || u.email}
                  </MenuItem>
                ))}
            </TextField>
          )}
          {canTakeIt && (
            <>
              <Button
                variant="outlined"
                fullWidth
                disabled={busy}
                onClick={() => me && assign(me.userId)}
              >
                {t("owner.board.deliverMyself")}
              </Button>
              <Typography variant="caption" color="error" sx={{ textAlign: "center" }}>
                {t("owner.board.deliveryByRequired")}
              </Typography>
            </>
          )}
          {next && (
            <Button
              variant="contained"
              size="large"
              fullWidth
              disabled={busy || needsAssignee || lineDetailsMissing}
              onClick={() => advance(next.to)}
            >
              {t(next.labelKey)}
            </Button>
          )}
          <Button
            variant="text"
            color="error"
            size="small"
            disabled={busy}
            onClick={async () => {
              if (
                await confirm({
                  title: t("owner.board.cancel"),
                  message: t("owner.board.cancelConfirm", {
                    number: order.orderNumber,
                  }),
                })
              )
                advance("CANCELLED");
            }}
          >
            {t("owner.board.cancel")}
          </Button>
        </Stack>
      </Box>
    </Card>
  );
}

/**
 * The "My deliveries" view: READY orders assigned to this staff member, one full-width row each
 * (not the sideways-scrolling lanes), each with everything a delivery needs and a single
 * Mark-delivered button. A vertical list beats the kanban lanes here because the delivery person
 * only ever does the last step — they don't advance the pipeline.
 */
function DeliveryList({
  orders,
  onError,
}: {
  orders: Order[];
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  if (orders.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
        {t("owner.board.noDeliveries")}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {orders.map((order) => (
        <DeliveryRow key={order.id} order={order} onError={onError} />
      ))}
    </Stack>
  );
}

function DeliveryRow({
  order,
  onError,
}: {
  order: Order;
  onError: (message: string) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const markDelivered = async () => {
    setBusy(true);
    try {
      // Same contract as the board card's `advance`: cancel any in-flight 30s poll before writing
      // the cache (D-057), and surface a failure rather than swallow it (D-062). mergeBoardOrder
      // drops the now-DELIVERED order, so the row disappears from the list.
      const updated = await updateOrderStatus(order.id, "DELIVERED", order.status);
      await queryClient.cancelQueries({ queryKey: BOARD_KEY });
      queryClient.setQueryData<Order[]>(BOARD_KEY, (prev) =>
        mergeBoardOrder(prev, updated),
      );
    } catch (err) {
      const conflict = axios.isAxiosError(err) && err.response?.status === 409;
      // Surface the server's own reason for a non-conflict error (e.g. the D-103 "enter the weight/
      // token" 400), falling back to the generic message; a stale-conflict keeps its own copy.
      onError(
        conflict
          ? t("owner.board.staleError")
          : errorMessage(err, t("owner.board.updateError")),
      );
      if (conflict) {
        await queryClient.invalidateQueries({ queryKey: ["board"] });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          sx={{ mb: 0.5 }}
        >
          <Typography fontWeight={700}>
            {t("common.orderNumber", { number: order.orderNumber })}
          </Typography>
          {/* Priceless order (D-069): nothing to collect a number for. */}
          {order.total != null && (
            <Typography variant="body2" color="text.secondary">
              {t("owner.board.toCollect")}:{" "}
              <strong>{formatMoney(order.total)}</strong>
            </Typography>
          )}
        </Stack>
        <Typography variant="body2">
          {order.customerName} · {order.customerPhone}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          {formatDateTime(order.createdAt)} · {order.paymentMethod} ·{" "}
          {order.paymentStatus}
        </Typography>
        {order.deliveryAddress && (
          <Typography variant="body2" sx={{ mt: 0.75 }}>
            <strong>{t("owner.board.deliverTo")}:</strong>{" "}
            {order.deliveryAddress}
          </Typography>
        )}
        <Box
          sx={{ mt: 0.75, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          {order.items.map((i, idx) => (
            <OrderItemLine
              key={`${i.productId}-${i.options ?? ""}-${idx}`}
              item={i}
            />
          ))}
        </Box>
        {order.notes && (
          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: "italic" }}>
            {t("owner.board.notes")}: {order.notes}
          </Typography>
        )}
      </CardContent>
      <Box sx={{ px: 2, pb: 2 }}>
        {/* Per-line tokens render in the item list above (D-102, read-only here). */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={busy}
          onClick={markDelivered}
        >
          {t("owner.board.markDelivered")}
        </Button>
      </Box>
    </Card>
  );
}
